import type { TenantInfo } from "@repo/shared/types";
import { Building2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { FullScreenLoader } from "@/components/full-screen-loader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { lookupTenantBySlug } from "@/lib/api/auth-login";
import { authClient } from "@/lib/auth-client";
import { useT } from "@/lib/i18n/use-t";
import { tenantUrl } from "@/lib/tenant-host";
import { T } from "./wrong-tenant-overlay.i18n";

/**
 * A stale session slug is re-read at most once per organization per minute (the
 * session cookie cache lifetime), so a session the rename did not reach falls back
 * to the overlay instead of reloading forever.
 */
const SESSION_REFRESH_KEY = "qp.renamedTenantSessionRefresh";
const SESSION_REFRESH_WINDOW_MS = 60_000;

function readSessionRefresh(): { slug: string; at: number } | null {
  try {
    const raw = sessionStorage.getItem(SESSION_REFRESH_KEY);
    return raw ? (JSON.parse(raw) as { slug: string; at: number }) : null;
  } catch {
    return null;
  }
}

function sessionRefreshExhausted(slug: string): boolean {
  const last = readSessionRefresh();
  return last?.slug === slug && Date.now() - last.at < SESSION_REFRESH_WINDOW_MS;
}

/** True when a refresh may run now; records it. */
function claimSessionRefresh(slug: string): boolean {
  if (sessionRefreshExhausted(slug)) return false;
  try {
    sessionStorage.setItem(SESSION_REFRESH_KEY, JSON.stringify({ slug, at: Date.now() }));
  } catch {
    return false;
  }
  return true;
}

/** The host tenant's lookup: a failed request is not the same as a missing tenant. */
type TargetState =
  | { status: "loading" }
  | { status: "found"; info: TenantInfo }
  | { status: "missing" }
  | { status: "error" };

type WrongTenantOverlayProps = {
  /** Tenant of the signed-in session. */
  sessionTenantSlug: string;
  /** Tenant whose host the user opened. */
  hostTenantSlug: string;
  /** Path + query + hash on this host to land on after signing in again. */
  returnTo: string;
  /**
   * During an OAuth sign-in: where to finish it as the signed-in organization
   * instead (the authorize endpoint). Replaces "go to my organization".
   */
  continueUrl?: string;
  /**
   * Resolves a slug to its organization: `null` when it does not exist, throws
   * when the lookup itself fails. Injectable for Storybook.
   */
  lookupTenant?: (slug: string) => Promise<TenantInfo | null>;
};

/**
 * QL-343 — full-page overlay shown on `<b>.<root>` while the session belongs to
 * tenant `a` (one session per browser). Signing out keeps the requested page;
 * going to the session's own tenant deliberately drops it, since the path
 * belongs to the other organization.
 */
export function WrongTenantOverlay({
  sessionTenantSlug,
  hostTenantSlug,
  returnTo,
  continueUrl,
  lookupTenant = lookupTenantBySlug,
}: WrongTenantOverlayProps) {
  const t = useT(T);
  const [current, setCurrent] = useState<TenantInfo | null>(null);
  const [target, setTarget] = useState<TargetState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    lookupTenant(sessionTenantSlug)
      .then((info) => !cancelled && setCurrent(info))
      // The session's own name is cosmetic: fall back to its slug
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [lookupTenant, sessionTenantSlug]);

  // `attempt` is a dependency so "Try again" re-runs the lookup
  useEffect(() => {
    let cancelled = false;
    setTarget({ status: "loading" });
    lookupTenant(hostTenantSlug)
      .then((info) => {
        if (!cancelled) setTarget(info ? { status: "found", info } : { status: "missing" });
      })
      .catch(() => {
        if (!cancelled) setTarget({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [lookupTenant, hostTenantSlug, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  // QL-351 — both slugs naming the same organization means it was renamed, not
  // that the user is in the wrong one: an old host moves to the current one, and
  // a session still carrying the old slug (cookie cache) is re-read once.
  const renamedSlug =
    target.status === "found" && current !== null && current.slug === target.info.slug
      ? target.info.slug
      : null;
  const [renameHandling, setRenameHandling] = useState<"leaving" | "overlay" | null>(null);
  useEffect(() => {
    if (!renamedSlug || renameHandling !== null) return;
    if (hostTenantSlug !== renamedSlug) {
      setRenameHandling("leaving");
      window.location.replace(tenantUrl(renamedSlug, returnTo || "/"));
      return;
    }
    if (!claimSessionRefresh(renamedSlug)) {
      setRenameHandling("overlay");
      return;
    }
    setRenameHandling("leaving");
    void authClient
      .getSession({ query: { disableCookieCache: true } })
      .finally(() => window.location.reload());
  }, [renamedSlug, renameHandling, hostTenantSlug, returnTo]);

  const currentName = current?.name ?? sessionTenantSlug;
  const targetName = target.status === "found" ? target.info.name : hostTenantSlug;
  const message = (
    target.status === "missing"
      ? t.targetMissing
      : target.status === "error"
        ? t.lookupFailed
        : t.signedInTo
  )(currentName, targetName);

  const signOutAndContinue = () => {
    // /clear signs out, wipes local state and forwards to /login?redirect=
    window.location.href =
      returnTo && returnTo !== "/" ? `/clear?redirect=${encodeURIComponent(returnTo)}` : "/clear";
  };

  const goToSessionTenant = () => {
    window.location.href = continueUrl ?? tenantUrl(sessionTenantSlug, "/");
  };

  if (renamedSlug && renameHandling !== "overlay") return <FullScreenLoader />;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background p-4">
      <Card
        className="w-full max-w-md p-8 space-y-6"
        role="alertdialog"
        aria-labelledby="wrong-tenant-title"
      >
        <div className="flex justify-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Building2 className="size-6" aria-hidden="true" />
          </div>
        </div>
        <div className="space-y-2 text-center">
          <h1 id="wrong-tenant-title" className="text-xl font-semibold">
            {t.title}
          </h1>
          <p className="text-sm text-muted-foreground">{message}</p>
          <p className="text-sm text-muted-foreground">{t.oneAtATime}</p>
        </div>
        <div className="space-y-2">
          {/* A failed lookup keeps the way forward: signing out is safe either way */}
          {target.status !== "missing" && (
            <Button
              className="w-full"
              onClick={signOutAndContinue}
              disabled={target.status === "loading"}
            >
              {t.signOutAndContinue}
            </Button>
          )}
          {target.status === "error" && (
            <Button variant="outline" className="w-full" onClick={retry}>
              {t.retry}
            </Button>
          )}
          <Button variant="outline" className="w-full" onClick={goToSessionTenant}>
            {continueUrl ? t.continueWith(currentName) : t.goTo}
          </Button>
        </div>
      </Card>
    </div>
  );
}
